/**
 * ServiceCategoriesPage.js
 * Verwaltung der Leistungskategorien (Haupt- und Unterkategorien)
 * Für Auswertungen nach Leistungsart - unabhängig von activity_type (Stundensatz)
 */
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../components/ui/card';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import CategoryInvoiceTextFields from '../components/CategoryInvoiceTextFields';
import {
  Plus,
  Edit,
  Trash2,
  ChevronDown,
  ChevronRight,
  FolderTree,
  Tag,
  ToggleLeft,
  ToggleRight,
  AlertTriangle,
  Info,
  RefreshCcw,
  Palette,
  GripVertical,
  CircleDot,
  X,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Preset colors for categories
const COLOR_PRESETS = [
  '#3B82F6', // Blue
  '#10B981', // Green  
  '#8B5CF6', // Purple
  '#F59E0B', // Amber
  '#EF4444', // Red
  '#EC4899', // Pink
  '#6B7280', // Gray
  '#14B8A6', // Teal
  '#F97316', // Orange
  '#84CC16', // Lime
];

export default function ServiceCategoriesPage() {
  const { user, token } = useAuth();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [includeInactive, setIncludeInactive] = useState(false);
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [parentForNew, setParentForNew] = useState(null);
  
  // Expanded state for tree
  const [expandedIds, setExpandedIds] = useState(new Set());
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    billable_default: true,
    sort_order: 0,
    color: '#3B82F6',
    employee_selectable_sub: false,
    invoice_title: '',
    invoice_template: '',
    effort_note: '',
    effort_note_locked: false,
    activities: [],
    activity_required: false,
  });
  const [deleteReason, setDeleteReason] = useState('');

  // Fetch categories
  const fetchCategories = useCallback(async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/service-categories?include_inactive=${includeInactive}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) throw new Error('Fehler beim Laden');
      const data = await response.json();
      setCategories(data);
      
      // Expand all by default
      const ids = new Set();
      data.forEach(cat => ids.add(cat.id));
      setExpandedIds(ids);
    } catch (error) {
      toast.error('Kategorien konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [token, includeInactive]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // Toggle expand
  const toggleExpand = (id) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Open create dialog
  const openCreateDialog = (parent = null) => {
    setParentForNew(parent);
    setFormData({
      name: '',
      billable_default: true,
      sort_order: parent ? (parent.children?.length || 0) + 1 : categories.length + 1,
      color: parent ? null : COLOR_PRESETS[categories.length % COLOR_PRESETS.length],
      employee_selectable_sub: false,
      invoice_title: '',
      invoice_template: '',
      effort_note: '',
      effort_note_locked: false,
      activities: [],
      activity_required: false,
    });
    setCreateDialogOpen(true);
  };

  // Open edit dialog
  const openEditDialog = (category) => {
    setSelectedCategory(category);
    setFormData({
      name: category.name,
      billable_default: category.billable_default,
      sort_order: category.sort_order,
      color: category.color || '',
      employee_selectable_sub: category.employee_selectable_sub || false,
      invoice_title: category.invoice_title || '',
      invoice_template: category.invoice_template || '',
      effort_note: category.effort_note || '',
      effort_note_locked: !!category.effort_note_locked,
      activities: Array.isArray(category.activities) ? category.activities : [],
      activity_required: !!category.activity_required,
    });
    setEditDialogOpen(true);
  };

  // Open delete dialog
  const openDeleteDialog = (category) => {
    setSelectedCategory(category);
    setDeleteReason('');
    setDeleteDialogOpen(true);
  };

  // Create category
  const handleCreate = async () => {
    if (!formData.name.trim()) {
      toast.error('Name ist erforderlich');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/service-categories`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          parent_id: parentForNew?.id || null,
          color: formData.color || null,
          // employee_selectable_sub nur bei Hauptkategorien mitsenden
          employee_selectable_sub: parentForNew ? false : formData.employee_selectable_sub,
          activities: parentForNew ? undefined : (formData.activities || []).filter((x) => (x.name || '').trim() || x.id),
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Fehler beim Erstellen');
      }

      toast.success(parentForNew ? 'Unterkategorie erstellt' : 'Hauptkategorie erstellt');
      setCreateDialogOpen(false);
      fetchCategories();
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Update category
  const handleUpdate = async () => {
    if (!formData.name.trim()) {
      toast.error('Name ist erforderlich');
      return;
    }

    try {
      const response = await fetch(
        `${API_URL}/api/service-categories/${selectedCategory.id}`,
        {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            name: formData.name,
            billable_default: formData.billable_default,
            sort_order: formData.sort_order,
            color: formData.color || null,
            // employee_selectable_sub nur bei Hauptkategorien (parent_id === null)
            ...(selectedCategory?.parent_id === null || selectedCategory?.parent_id === undefined
              ? { employee_selectable_sub: formData.employee_selectable_sub }
              : {}),
            // L1: Rechnungstext (Haupt- und Unterkategorie), Tätigkeiten nur Hauptkategorie
            invoice_title: formData.invoice_title,
            invoice_template: formData.invoice_template,
            effort_note: formData.effort_note,
            effort_note_locked: formData.effort_note_locked,
            ...(selectedCategory?.parent_id
              ? {}
              : {
                  activities: (formData.activities || []).filter((x) => (x.name || '').trim() || x.id),
                  activity_required: formData.activity_required,
                }),
          }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Fehler beim Aktualisieren');
      }

      toast.success('Kategorie aktualisiert');
      setEditDialogOpen(false);
      fetchCategories();
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Deactivate category
  const handleDeactivate = async (category) => {
    try {
      const response = await fetch(
        `${API_URL}/api/service-categories/${category.id}/deactivate`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Fehler beim Deaktivieren');
      }

      toast.success('Kategorie deaktiviert');
      fetchCategories();
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Reactivate category
  const handleReactivate = async (category) => {
    try {
      const response = await fetch(
        `${API_URL}/api/service-categories/${category.id}/reactivate`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Fehler beim Reaktivieren');
      }

      toast.success('Kategorie reaktiviert');
      fetchCategories();
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Delete category
  const handleDelete = async () => {
    if (deleteReason.trim().length < 5) {
      toast.error('Löschgrund muss mindestens 5 Zeichen haben');
      return;
    }

    try {
      const response = await fetch(
        `${API_URL}/api/service-categories/${selectedCategory.id}?reason=${encodeURIComponent(deleteReason)}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!response.ok) {
        const error = await response.json();
        // Handle usage warning
        if (error.detail?.message) {
          toast.error(error.detail.message);
          return;
        }
        throw new Error(error.detail || 'Fehler beim Löschen');
      }

      toast.success('Kategorie gelöscht');
      setDeleteDialogOpen(false);
      fetchCategories();
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Count stats
  const totalCategories = categories.reduce(
    (acc, cat) => acc + 1 + (cat.children?.length || 0),
    0
  );
  const activeCategories = categories.reduce(
    (acc, cat) =>
      acc +
      (cat.is_active ? 1 : 0) +
      (cat.children?.filter((c) => c.is_active).length || 0),
    0
  );

  // Render category item
  const renderCategoryItem = (category, isChild = false) => {
    const isExpanded = expandedIds.has(category.id);
    const hasChildren = category.children?.length > 0;
    const isInactive = !category.is_active;

    return (
      <div
        key={category.id}
        className={`${isChild ? 'ml-8 border-l border-border pl-4' : ''}`}
        data-testid={`category-item-${category.id}`}
      >
        <div
          className={`flex items-center justify-between p-3 rounded-lg transition-colors ${
            isInactive
              ? 'bg-muted/50 opacity-60'
              : 'bg-muted/80 hover:bg-muted/80'
          }`}
        >
          <div className="flex items-center gap-3">
            {/* Expand/collapse for main categories */}
            {!isChild && hasChildren ? (
              <button
                onClick={() => toggleExpand(category.id)}
                className="p-1 hover:bg-muted-foreground rounded"
                data-testid={`toggle-expand-${category.id}`}
              >
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                )}
              </button>
            ) : (
              <div className="w-6" />
            )}

            {/* Color indicator */}
            {category.color && (
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: category.color }}
              />
            )}

            {/* Category name */}
            <span className={`font-medium ${isInactive ? 'line-through text-muted-foreground' : 'text-foreground'}`}>
              {category.name}
            </span>

            {/* Badges */}
            <div className="flex items-center gap-2">
              {isInactive && (
                <Badge variant="secondary" className="bg-muted text-muted-foreground text-xs">
                  Deaktiviert
                </Badge>
              )}
              <Badge
                variant="outline"
                className={`text-xs ${
                  category.billable_default
                    ? 'border-green-600 text-green-600 dark:text-green-400'
                    : 'border-border text-muted-foreground'
                }`}
              >
                {category.billable_default ? 'Abrechenbar' : 'Nicht abrechenbar'}
              </Badge>
              {!isChild && hasChildren && (
                <Badge variant="outline" className="text-xs border-border text-muted-foreground">
                  {category.children.length} Unterkategorien
                </Badge>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1">
            {!isChild && category.is_active && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => openCreateDialog(category)}
                className="h-8 text-muted-foreground hover:text-foreground"
                title="Unterkategorie hinzufügen"
                data-testid={`add-subcategory-${category.id}`}
              >
                <Plus className="h-4 w-4" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => openEditDialog(category)}
              className="h-8 text-muted-foreground hover:text-foreground"
              title="Bearbeiten"
              data-testid={`edit-category-${category.id}`}
            >
              <Edit className="h-4 w-4" />
            </Button>
            {category.is_active ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDeactivate(category)}
                className="h-8 text-muted-foreground hover:text-amber-600 dark:text-amber-400"
                title="Deaktivieren"
                data-testid={`deactivate-category-${category.id}`}
              >
                <ToggleRight className="h-4 w-4" />
              </Button>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleReactivate(category)}
                className="h-8 text-muted-foreground hover:text-green-600 dark:text-green-400"
                title="Reaktivieren"
                data-testid={`reactivate-category-${category.id}`}
              >
                <ToggleLeft className="h-4 w-4" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => openDeleteDialog(category)}
              className="h-8 text-muted-foreground hover:text-red-500"
              title="Löschen"
              data-testid={`delete-category-${category.id}`}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Children */}
        {!isChild && hasChildren && isExpanded && (
          <div className="mt-2 space-y-2">
            {category.children.map((child) => renderCategoryItem(child, true))}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="p-6 flex justify-center items-center min-h-96">
        <RefreshCcw className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="service-categories-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <FolderTree className="h-6 w-6" />
            Leistungskategorien
          </h1>
          <p className="text-muted-foreground mt-1">
            Kategorisierung für Auswertungen nach Leistungsart
          </p>
        </div>
        <Button
          onClick={() => openCreateDialog(null)}
          className="bg-blue-600 hover:bg-blue-700"
          data-testid="create-main-category-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Hauptkategorie
        </Button>
      </div>

      {/* Info card */}
      <Card className="bg-card/50 border-border">
        <CardContent className="pt-4">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-muted-foreground">
              <p className="font-medium text-muted-foreground mb-1">Hinweise zur Verwendung</p>
              <ul className="list-disc list-inside space-y-1">
                <li>
                  Leistungskategorien dienen der Auswertung nach Leistungsart (z.B. Regelbetreuung, Projekte, Schulungen)
                </li>
                <li>
                  <strong className="text-muted-foreground">Unabhängig von Stundensätzen:</strong> Die Tätigkeitsart (activity_type) steuert weiterhin die Abrechnung
                </li>
                <li>
                  <strong className="text-muted-foreground">Abrechenbar-Kennzeichen:</strong> Gibt den Standard vor, kann pro Eintrag überschrieben werden
                </li>
                <li>
                  <strong className="text-amber-600 dark:text-amber-400">Deaktivieren statt Löschen:</strong> Deaktivierte Kategorien bleiben an bestehenden Einträgen gültig, sind aber nicht mehr neu wählbar
                </li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats & Filter */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Badge variant="outline" className="border-border text-muted-foreground px-3 py-1">
            {totalCategories} Kategorien
          </Badge>
          <Badge variant="outline" className="border-green-600 text-green-600 dark:text-green-400 px-3 py-1">
            {activeCategories} Aktiv
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          <Label htmlFor="show-inactive" className="text-sm text-muted-foreground">
            Deaktivierte anzeigen
          </Label>
          <Switch
            id="show-inactive"
            checked={includeInactive}
            onCheckedChange={setIncludeInactive}
            data-testid="toggle-inactive-switch"
          />
        </div>
      </div>

      {/* Category tree */}
      <div className="space-y-3" data-testid="category-tree">
        {categories.length === 0 ? (
          <Card className="bg-card/50 border-border">
            <CardContent className="py-12 text-center">
              <FolderTree className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Keine Kategorien vorhanden</p>
              <Button
                onClick={() => openCreateDialog(null)}
                variant="outline"
                className="mt-4"
              >
                <Plus className="h-4 w-4 mr-2" />
                Erste Kategorie erstellen
              </Button>
            </CardContent>
          </Card>
        ) : (
          categories.map((cat) => renderCategoryItem(cat))
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog
        open={createDialogOpen || editDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setCreateDialogOpen(false);
            setEditDialogOpen(false);
          }
        }}
      >
        <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-foreground">
              {editDialogOpen
                ? 'Kategorie bearbeiten'
                : parentForNew
                ? `Unterkategorie für "${parentForNew.name}"`
                : 'Neue Hauptkategorie'}
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              {editDialogOpen
                ? 'Ändern Sie die Eigenschaften der Kategorie'
                : 'Geben Sie die Details der neuen Kategorie ein'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="cat-name" className="text-muted-foreground">
                Name *
              </Label>
              <Input
                id="cat-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="z.B. Regelbetreuung, Schulungen, ..."
                className="bg-muted border-border text-foreground"
                data-testid="category-name-input"
              />
            </div>

            {/* Billable default */}
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-muted-foreground">Abrechenbar (Standard)</Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Kann pro Eintrag überschrieben werden
                </p>
              </div>
              <Switch
                checked={formData.billable_default}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, billable_default: checked })
                }
                data-testid="billable-default-switch"
              />
            </div>

            {/* Color (only for main categories) */}
            {(!editDialogOpen || !selectedCategory?.parent_id) && !parentForNew && (
              <div className="space-y-2">
                <Label className="text-muted-foreground">Farbe</Label>
                <div className="flex items-center gap-2 flex-wrap">
                  {COLOR_PRESETS.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setFormData({ ...formData, color })}
                      className={`w-8 h-8 rounded-full border-2 transition-transform ${
                        formData.color === color
                          ? 'border-white scale-110'
                          : 'border-transparent hover:scale-105'
                      }`}
                      style={{ backgroundColor: color }}
                      data-testid={`color-${color}`}
                    />
                  ))}
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, color: '' })}
                    className={`w-8 h-8 rounded-full border-2 flex items-center justify-center ${
                      !formData.color
                        ? 'border-white bg-muted'
                        : 'border-border bg-muted hover:bg-muted'
                    }`}
                    title="Keine Farbe"
                  >
                    <X className="h-4 w-4 text-muted-foreground" />
                  </button>
                </div>
              </div>
            )}

            {/* Employee selectable sub (only for main categories) */}
            {(!editDialogOpen || !selectedCategory?.parent_id) && !parentForNew && (
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <div>
                  <Label className="text-muted-foreground">Unterkategorie durch Mitarbeitende wählbar</Label>
                  <p className="text-xs text-muted-foreground mt-0.5 max-w-[320px]">
                    Wenn aktiviert, wählen Mitarbeitende bei der Buchung die Unterkategorie selbst
                    (z.B. für Regelbetreuung). Sonst wird die am Auftrag hinterlegte Kategorie verwendet.
                  </p>
                </div>
                <Switch
                  checked={formData.employee_selectable_sub}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, employee_selectable_sub: checked })
                  }
                  data-testid="employee-selectable-sub-switch"
                />
              </div>
            )}

            {/* L1: Rechnungstext */}
            <CategoryInvoiceTextFields
              isMain={editDialogOpen ? !selectedCategory?.parent_id : !parentForNew}
              value={formData}
              parentCategory={
                editDialogOpen
                  ? categories.find((c) => c.id === selectedCategory?.parent_id) || null
                  : parentForNew
              }
              onChange={(patch) => setFormData((prev) => ({ ...prev, ...patch }))}
            />

            {/* Sort order */}
            <div className="space-y-2">
              <Label htmlFor="sort-order" className="text-muted-foreground">
                Sortierung
              </Label>
              <Input
                id="sort-order"
                type="number"
                value={formData.sort_order}
                onChange={(e) =>
                  setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })
                }
                className="bg-muted border-border text-foreground w-24"
                data-testid="sort-order-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setCreateDialogOpen(false);
                setEditDialogOpen(false);
              }}
              className="border-border"
            >
              Abbrechen
            </Button>
            <Button
              onClick={editDialogOpen ? handleUpdate : handleCreate}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="save-category-btn"
            >
              {editDialogOpen ? 'Speichern' : 'Erstellen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Alert Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-foreground flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
              Kategorie löschen?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground">
              Möchten Sie die Kategorie "{selectedCategory?.name}" wirklich löschen?
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          {/* Content outside AlertDialogDescription to avoid HTML nesting violation */}
          <div className="space-y-4 py-2">
            <div className="bg-amber-900/20 border border-amber-700/50 rounded-lg p-3">
              <p className="text-amber-600 dark:text-amber-400 text-sm">
                <strong>Hinweis:</strong> Das Löschen ist nur möglich, wenn die Kategorie nicht verwendet wird.
                Bei verwendeten Kategorien nutzen Sie bitte die <strong>Deaktivieren</strong>-Funktion.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="delete-reason" className="text-muted-foreground">
                Löschgrund (mind. 5 Zeichen) *
              </Label>
              <Input
                id="delete-reason"
                value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value)}
                placeholder="Grund für die Löschung..."
                className="bg-muted border-border text-foreground"
                data-testid="delete-reason-input"
              />
            </div>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel className="border-border text-muted-foreground">
              Abbrechen
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleteReason.trim().length < 5}
              className="bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="confirm-delete-btn"
            >
              Endgültig löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
